/**
 * Pure classification of dynamic block properties — no ODA, no DOM, no logger.
 *
 * Kept apart from dynamic.ts (which talks to the WASM engine) so this logic stays
 * trivially testable and free of import-time side effects.
 */

/** A dynamic property, flattened to plain JS values for the UI layer. */
export interface DynBlockPropertyInfo {
  /** Property name as authored in AutoCAD, e.g. "Etat d'inversion1". Shown as-is (not translatable). */
  name: string;
  /** Current value, stringified. */
  value: string;
  /** Allowed values when the property is constrained (flip, visibility, preset list); empty when free. */
  allowedValues: string[];
  readOnly: boolean;
  /** False when AutoCAD hides the property from its palette — we hide it too. */
  show: boolean;
  /** True for numeric properties (positions, distances, angles). */
  isNumeric: boolean;
  /** ODA units: 0 none, 1 angular (radians), 2 distance, 3 area. */
  unitsType: number;
}

/** ODA's UnitsType for angular properties — their values are stored in radians. */
export const UNITS_ANGULAR = 1;

/** An X/Y property pair that can be dragged as a single point. */
export interface DynBlockPointGroup {
  /** Shared prefix, e.g. "Position1" for "Position1 X" / "Position1 Y". */
  baseName: string;
  nameX: string;
  nameY: string;
}

export interface DynBlockPropertyGroups {
  points: DynBlockPointGroup[];
  /** Exactly two allowed values → a single tap toggles them. */
  flips: DynBlockPropertyInfo[];
  /** Three or more allowed values → the user picks from a list. */
  lists: DynBlockPropertyInfo[];
  /**
   * Free rotation parameters.
   *
   * Their grip is provisional: ODA's web build exposes the parameter's base point
   * (the "Origin" property) but not its axis, so a grip cannot be placed where
   * AutoCAD puts it nor dragged around the right centre. Instead the grip only opens
   * a dial — turning a block by eye beats typing degrees, and the dial gives that
   * back without needing the axis.
   */
  angles: DynBlockPropertyInfo[];
  /**
   * The remaining free numeric scalars (linear distances).
   *
   * No grip at all, for the same missing-axis reason and with no equivalent of the
   * dial to fall back on: they are edited as numeric fields in the panel.
   */
  numbers: DynBlockPropertyInfo[];
}

/** Suffixes AutoCAD appends to the two coordinates of a point parameter. */
const X_SUFFIX = " X";
const Y_SUFFIX = " Y";

/**
 * Split dynamic properties into what the canvas can manipulate.
 *
 * Hidden and read-only properties are dropped: AutoCAD does not offer them either.
 * Coordinates are matched by name because ODA exposes a point parameter as two
 * independent scalar properties — there is no API tying them back together.
 */
export function groupDynBlockProperties(
  properties: DynBlockPropertyInfo[],
): DynBlockPropertyGroups {
  const editable = properties.filter((prop) => prop.show && !prop.readOnly);
  const points: DynBlockPointGroup[] = [];
  const consumed = new Set<string>();

  for (const prop of editable) {
    // A constrained coordinate is a preset to pick, not a point to drag.
    if (!prop.isNumeric || prop.allowedValues.length > 0) continue;
    if (!prop.name.endsWith(X_SUFFIX)) continue;

    const baseName = prop.name.slice(0, -X_SUFFIX.length);
    const partner = editable.find(
      (candidate) =>
        candidate.name === `${baseName}${Y_SUFFIX}` &&
        candidate.isNumeric &&
        candidate.allowedValues.length === 0,
    );
    if (!partner) continue;

    points.push({ baseName, nameX: prop.name, nameY: partner.name });
    consumed.add(prop.name);
    consumed.add(partner.name);
  }

  const remaining = editable.filter((prop) => !consumed.has(prop.name));
  const scalars = remaining.filter(
    (prop) => prop.allowedValues.length === 0 && prop.isNumeric,
  );

  return {
    points,
    flips: remaining.filter((prop) => prop.allowedValues.length === 2),
    lists: remaining.filter((prop) => prop.allowedValues.length > 2),
    angles: scalars.filter((prop) => prop.unitsType === UNITS_ANGULAR),
    numbers: scalars.filter((prop) => prop.unitsType !== UNITS_ANGULAR),
  };
}

/**
 * True for the block's visibility parameter.
 *
 * ODA exposes no flag for it, so we match the name AutoCAD gives it — which follows
 * the authoring language ("Visibility1", "Visibilité1", "Visibilidad1"…).
 */
export function isVisibilityProperty(property: DynBlockPropertyInfo): boolean {
  return /^visibilit/i.test(property.name);
}

/**
 * Glyph of the canvas grip a panel row belongs to — same silhouettes as
 * transform/handleShapes, so a row and its grip are recognisably the same thing.
 * Null for properties edited only in the panel (free numeric scalars).
 */
export type DynBlockRowGlyph =
  | "square"
  | "triangleDown"
  | "doubleArrow"
  | "angleSector"
  | null;

/** One line of the properties panel. */
export interface DynBlockPanelRow {
  /** Unique within a block — the property name, or the point's base name. */
  key: string;
  label: string;
  glyph: DynBlockRowGlyph;
  /** Single-value row: flip, value list, or free numeric scalar. */
  property: DynBlockPropertyInfo | null;
  /** Point row: the two coordinates, edited side by side on one line. */
  point: { x: DynBlockPropertyInfo; y: DynBlockPropertyInfo } | null;
}

/**
 * Rows to show in the panel, in display order.
 *
 * Every editable parameter appears, grips included: a square grip on the canvas with
 * nothing to match it in the panel is unexplainable, and the coordinates it writes
 * are worth reading. Point parameters collapse into a single row so their X and Y sit
 * on the same line under one name.
 *
 * The visibility parameter comes first — it drives which other properties are even
 * relevant — and the rest keeps the grouping order.
 */
export function getPanelRows(
  properties: DynBlockPropertyInfo[],
): DynBlockPanelRow[] {
  const { points, flips, lists, angles, numbers } =
    groupDynBlockProperties(properties);
  const byName = new Map(properties.map((prop) => [prop.name, prop]));

  const single = (
    prop: DynBlockPropertyInfo,
    glyph: DynBlockRowGlyph,
  ): DynBlockPanelRow => ({
    key: prop.name,
    label: prop.name,
    glyph,
    property: prop,
    point: null,
  });

  const pointRows = points.flatMap((point): DynBlockPanelRow[] => {
    const x = byName.get(point.nameX);
    const y = byName.get(point.nameY);
    if (!x || !y) return [];
    return [
      {
        key: point.baseName,
        label: point.baseName,
        glyph: "square",
        property: null,
        point: { x, y },
      },
    ];
  });

  const rows = [
    ...lists.map((prop) => single(prop, "triangleDown")),
    ...flips.map((prop) => single(prop, "doubleArrow")),
    ...pointRows,
    ...angles.map((prop) => single(prop, "angleSector")),
    ...numbers.map((prop) => single(prop, null)),
  ];

  const isVisibilityRow = (row: DynBlockPanelRow): boolean =>
    !!row.property && isVisibilityProperty(row.property);

  // Stable partition: visibility first, everything else in grouping order.
  return [
    ...rows.filter(isVisibilityRow),
    ...rows.filter((row) => !isVisibilityRow(row)),
  ];
}

/**
 * Reconcile text values recovered from the block's representation cache with the
 * properties they belong to.
 *
 * The cache is keyed by eval-graph node id, not by property name, and ODA's web
 * build exposes no link between the two — the only thing tying them together is
 * their shared order (see blocks/dynamicRepData.ts). A positional match is only
 * trustworthy if it checks out, so it is rejected wholesale unless both lists have
 * the same length and every value belongs to its property's allowed values. An
 * unknown state is better than a plausible wrong one.
 *
 * @param textIndexes indexes, in order, of the text-valued properties
 * @returns value per property index; empty when the match cannot be trusted
 */
export function alignCachedTextValues(
  properties: DynBlockPropertyInfo[],
  textIndexes: number[],
  cachedValues: string[],
): Map<number, string> {
  const aligned = new Map<number, string>();
  if (!textIndexes.length || cachedValues.length !== textIndexes.length)
    return aligned;

  const consistent = textIndexes.every((propertyIndex, cacheIndex) => {
    const allowed = properties[propertyIndex]?.allowedValues ?? [];
    return !allowed.length || allowed.includes(cachedValues[cacheIndex]);
  });
  if (!consistent) return aligned;

  textIndexes.forEach((propertyIndex, cacheIndex) => {
    aligned.set(propertyIndex, cachedValues[cacheIndex]);
  });
  return aligned;
}
